import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Gift, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import api from "@/lib/api";

export default function Login() {
  const { user, login, verify2fa, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(location.state?.register ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(location.state?.twofa || null); // { challenge } when a 2FA code is required
  const [code, setCode] = useState("");
  const [promoCode, setPromoCode] = useState(""); // Optional promo code on signup
  const [inviter, setInviter] = useState(null); // referrer name when arriving via an invite link
  const [topGlads, setTopGlads] = useState([]);

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  useEffect(() => {
    api.get("/public/top-gladiators")
      .then((r) => setTopGlads((r.data?.top || []).slice(0, 5)))
      .catch(() => setTopGlads([]));
  }, []);

  useEffect(() => {
    // Only check referral code on initial mount
    const ref = new URLSearchParams(window.location.search).get("ref");
    
    if (ref) {
      // Fresh referral from URL - store it and switch to register
      localStorage.setItem("lanista_ref", ref);
      setMode("register");
      
      api.get(`/referral/lookup?code=${encodeURIComponent(ref.trim())}`)
        .then((r) => { if (r.data.valid) setInviter(r.data.referrer_name); })
        .catch(() => {});
    } else {
      // Check if there's a stored referral (but don't force register mode)
      const storedRef = localStorage.getItem("lanista_ref");
      if (storedRef) {
        api.get(`/referral/lookup?code=${encodeURIComponent(storedRef.trim())}`)
          .then((r) => { if (r.data.valid) setInviter(r.data.referrer_name); })
          .catch(() => {});
      }
    }
  }, []); // Run only once on mount

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await login(email, password);
        if (res && res.twofa_required) { setTwofa({ challenge: res.challenge }); setBusy(false); return; }
      } else {
        const res = await register(email, password, name);
        if (res?.invited_by) {
          toast.success(`You were invited by ${res.invited_by}! 🎉`, {
            description: "You'll both earn escalating rewards as you battle through the arena.",
          });
        }
        
        // Try to redeem promo code if provided
        if (promoCode.trim()) {
          try {
            const { data } = await api.post("/promo-codes/redeem", { code: promoCode.trim() });
            toast.success(`Promo code redeemed! ${data.rewards.join(", ")}`, { icon: <Gift /> });
          } catch (err) {
            // Don't block signup if promo code fails
            toast.warning(`Promo code issue: ${apiErr(err)}`);
          }
        }
      }
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verify2fa(twofa.challenge, code.trim());
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="relative hidden lg:flex lg:w-1/2 items-end overflow-hidden">
        <img src="https://images.unsplash.com/photo-1511163262182-1b04e5fa4caa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYW5jaWVudCUyMHJvbWFuJTIwY29sb3NzZXVtfGVufDB8fHx8MTc4MTQwMDUzMXww&ixlib=rb-4.1.0&q=85"
             alt="Colosseum" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/80 to-zinc-950/30" />
        <div className="relative p-12 z-10 w-full">
          {topGlads.length > 0 && (
            <div data-testid="login-rankings" className="mb-8 max-w-md">
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={15} className="text-amber-500/80" />
                <span className="font-cinzel text-[11px] uppercase tracking-[0.25em] text-amber-300/80">Top Gladiators</span>
              </div>
              <div className="space-y-1.5">
                {topGlads.map((g) => (
                  <div key={g.rank} data-testid={`login-rank-${g.rank}`}
                    className="flex items-center gap-3 backdrop-blur-sm bg-zinc-950/40 border border-amber-900/30 rounded-sm px-3 py-2">
                    <span className={`font-cinzel text-sm w-4 shrink-0 ${g.rank === 1 ? "text-amber-400" : "text-zinc-500"}`}>{g.rank}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-zinc-100 leading-tight">{g.name}</span>
                      <span className="block text-[10px] text-zinc-500 leading-tight">{g.cls} · {g.owner_name}</span>
                    </span>
                    <span className="font-cinzel text-amber-300 text-sm shrink-0">{g.rating}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <h1 className="font-cinzel text-5xl font-black text-amber-400 tracking-wide uppercase leading-tight">Lanista</h1>
          <p className="font-cinzel text-xl text-amber-200/80 tracking-[0.3em] uppercase mt-1">Gladiator Manager</p>
          <p className="mt-4 text-zinc-400 max-w-md font-manrope">Recruit raw fighters. Train them through their prime. Send them to glory before age claims them.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center gap-3 mb-8">
            <Swords className="text-amber-500" size={32} />
            <span className="font-cinzel text-2xl text-amber-300 uppercase tracking-widest">Ludus</span>
          </div>
          <h2 className="font-cinzel text-2xl text-zinc-100 mb-1">{twofa ? "Two-Factor Verification" : mode === "login" ? "Enter the Ludus" : "Found your Ludus"}</h2>
          <p className="text-zinc-500 text-sm mb-6">{twofa ? "Enter the 6-digit code from your authenticator app." : mode === "login" ? "Return to your gladiators." : "Begin your training school."}</p>

          {twofa ? (
            <form onSubmit={submitCode} className="space-y-4" data-testid="twofa-form">
              <input data-testid="twofa-code-input" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus
                inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code (or backup code)"
                className="w-full bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none rounded-sm tracking-[0.3em] text-center" />
              <button data-testid="twofa-submit-btn" disabled={busy} type="submit"
                className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 hover:border-amber-500 transition-all disabled:opacity-50">
                {busy ? "…" : "Verify & Enter"}
              </button>
              <button type="button" data-testid="twofa-cancel-btn" onClick={() => { setTwofa(null); setCode(""); setPassword(""); }}
                className="w-full text-zinc-500 hover:text-amber-400 underline text-sm transition-colors">
                Back to login
              </button>
            </form>
          ) : (
          <>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && inviter && (
              <div data-testid="referral-invite-banner"
                className="flex items-start gap-2.5 border border-amber-600/50 bg-amber-950/25 px-3.5 py-3 rounded-sm">
                <Gift size={16} className="text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-100 leading-snug">
                  You were invited by <span className="font-cinzel text-amber-300">{inviter}</span> 🎉
                  <span className="block text-xs text-amber-200/70 mt-0.5">Create your ludus and you&apos;ll both earn escalating rewards as you battle.</span>
                </p>
              </div>
            )}
            {mode === "register" && (
              <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Lanista name"
                className="w-full bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none rounded-sm" />
            )}
            <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email"
              className="w-full bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none rounded-sm" />
            <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password"
              className="w-full bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none rounded-sm" />
            {mode === "register" && (
              <input
                data-testid="register-promo-input"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Promo code (optional)"
                maxLength={30}
                className="w-full bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 font-mono placeholder-zinc-600 focus:border-amber-600 outline-none rounded-sm"
              />
            )}
            <button data-testid="login-submit-btn" disabled={busy} type="submit"
              className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 hover:border-amber-500 transition-all disabled:opacity-50">
              {busy ? "…" : mode === "login" ? "Enter Arena" : "Create School"}
            </button>
          </form>
          <div data-testid="google-login-retired-notice" className="mt-5 border border-zinc-800 bg-zinc-950 px-4 py-3 text-sm text-zinc-400">
            <p className="text-zinc-200">Google sign-in has been removed.</p>
            <p className="mt-1">If you previously used Google, reset your password to continue.</p>
            <button data-testid="google-login-retired-reset-link" type="button" onClick={() => navigate("/forgot-password")}
              className="mt-2 text-amber-400 underline transition-colors hover:text-amber-300">
              Reset your password
            </button>
          </div>
          </>
          )}

          {mode === "login" && !twofa && (
            <p className="text-center text-sm mt-4">
              <button data-testid="forgot-password-link" type="button" onClick={() => navigate("/forgot-password")}
                className="text-zinc-500 hover:text-amber-400 underline transition-colors">
                Forgot your password?
              </button>
            </p>
          )}

          <p className="text-center text-sm text-zinc-500 mt-6">
            {!twofa && (<>
            {mode === "login" ? "New to the games?" : "Already a lanista?"}{" "}
            <button data-testid="toggle-auth-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-amber-400 hover:text-amber-300 underline">
              {mode === "login" ? "Found a Ludus" : "Sign in"}
            </button>
            </>)}
          </p>
        </div>
      </div>
    </div>
  );
}
